import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';

import Loader from '../../components/Loader';
import TimingModal from '../../components/TimingModal';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import FileDropzone from "../../components/FileDropzone.jsx";
import ImageUpload from "../../components/ImageUpload";
import TagsInput from "../../components/TagsInput";
import LocationPicker from "../../components/LocationPicker";
import { providerType } from "./providerTypes";


const CompanyForm = ({ type = 'seller' }) => {
  const cfg = providerType(type);
  const isFacility = type === 'facility';
  const [areaSuggestions, setAreaSuggestions] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("info");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [isLoader, setIsLoader] = useState(false);
  const [pageLoader, setPageLoader] = useState(false);
  const [categories, setCategories] = useState([]);
  const [sub_categories, setSubCategories] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    secondary_phone: '',
    password: '',
    location: '',
    lat: null,
    lng: null,
    categories: null,
    sub_category_ids: null,
    emirates: null,
    areas: [],
    about: '',
    certificate:null,
    license: null,
    picture: null
  });

  const [area_coordinates, setAreaCoordinates] = useState();

  const handleCloseModal = () => {
    setIsModalOpen(false);
    getSingleData();
  };

  // Load branch data if editing an existing branch
  useEffect(() => {
    if (id) {
      getSingleData();
    }
    getCategories();
    getEmirates();
    if (isFacility) {
      ApiService.request({ method: 'GET', url: 'facility-areas' })
        .then(({ data }) => data.status && setAreaSuggestions(data.data))
        .catch(() => {});
    }
  }, [id]);

  // Load subcategories when categories change
  useEffect(() => {
    if (formData.categories && formData.categories.length > 0) {
      getSubCategories();
    } else {
      setSubCategories([]);
    }
  }, [formData.categories]);




  const getSingleData = async () => {
    try {
      setPageLoader(true);
      const response = await ApiService.request({
        method: 'GET',
        url: `companies/${id}`,
        data: {get_for: 'edit'}
      });

      const data = response.data;
      if (data.status) {
        const company = data.data;
        delete company.password;
        setFormData({
          ...company,
          categories: company.categories,
          sub_category_ids: company.sub_categories,
          emirates: company.emirates,
          areas: company.areas || [],
          about: company.about || '',
          license: company.license_url,
          certificate: company.certificate_url,
        });
      } else {
        toast.error(data.message);
      }
      setPageLoader(false);
    } catch (error) {
      setPageLoader(false);
    }
  };

  const getCategories = async () => {
    try {

      setPageLoader(true);
      const response = await ApiService.request({
        method: 'GET',
        url: `categories-list`,
      });

      const data = response.data;

      if (data.status) {
        setCategories(data.data);
      } else {
        toast.error(data.message);
      }
      setPageLoader(false);
    } catch (error) {
      console.log(error);
      setPageLoader(false);
    }
  };

  const getSubCategories = async () => {
    try {
      setPageLoader(true);
      // Get subcategories for all selected categories
      if (formData.categories && formData.categories.length > 0) {
        const subCategoriesPromises = formData.categories.map(categoryId => 
          ApiService.request({
            method: 'GET',
            url: `categories/${categoryId}`,
          })
        );
        
        const responses = await Promise.all(subCategoriesPromises);
        const allSubCategories = [];
        
        responses.forEach(response => {
          if (response.data.status) {
            allSubCategories.push(...response.data.sub_categories);
          }
        });
        
        // Remove duplicates based on _id
        const uniqueSubCategories = allSubCategories.filter((category, index, self) => 
          index === self.findIndex(c => c._id === category._id)
        );
        
        setSubCategories(uniqueSubCategories);
      } else {
        setSubCategories([]);
      }
      setPageLoader(false);
    } catch (error) {
      setPageLoader(false);
    }
  };

  const getEmirates = async () => {
    try {
      setPageLoader(true);
      const response = await ApiService.request({
        method: 'GET',
        url: `emirates-list`,
      });

      const data = response.data;
      if (data.status) {
        setEmirates(data.data);
      } else {
        toast.error(data.message);
      }
      setPageLoader(false);
    } catch (error) {
      console.log(error);
      setPageLoader(false);
    }
  };
  
  const [newFiles, setNewFiles] = useState({
    license: [],
    certificate: []
  });

  const createSetFiles = (key) => (valueOrFn) => {
    setNewFiles((prev) => {
      const currentFiles = prev[key] || [];
      const updatedFiles = typeof valueOrFn === 'function' 
        ? valueOrFn(currentFiles) 
        : valueOrFn;
      return { ...prev, [key]: updatedFiles };
    });
  };

  const updateOldFiles = (newOldFiles) => {
    setFormData({ ...formData, old_files: newOldFiles });
  };

  const handleImageChange = (name, file) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: file,
    }));
  };


  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoader(true);

    try {
      const form = new FormData();
      
      // Handle regular form fields
      Object.entries(formData).forEach(([key, value]) => {
        if (key !== 'picture' && key !== 'license' && key !== 'certificate') {
          if (value !== null && value !== undefined) {
            if (key === 'categories' && Array.isArray(value)) {
              form.append(key, value.join(','));
            } else if (key === 'sub_category_ids' && Array.isArray(value)) {
              form.append(key, value.join(','));
            } else if (key === 'emirates' && Array.isArray(value)) {
              form.append(key, value.join(','));
            } else if (key === 'areas') {
              form.append(key, JSON.stringify(value || []));
            } else {
              form.append(key, value);
            }
          }
        }
      });

      // Handle file uploads
      if (formData.picture instanceof File) {
        form.append('picture', formData.picture);
      }
      
      // Handle existing files from FileDropzone
      if (newFiles) {
        Object.entries(newFiles).forEach(([key, fileArray]) => {
          if (fileArray && fileArray.length > 0) {
            fileArray.forEach(file => {
              if (file instanceof File) {
                form.append(key, file);
              }
            });
          }
        });
      }

      form.set('type', cfg.type);

      const response = await ApiService.request({
        method: 'POST',
        url: `companies/createOrUpdate/${id || ''}`,
        data: form,
      });

      const data = response.data;
      if (data.status) {
        toast.success(data.message);
        navigate(cfg.basePath);

      } else {
        toast.error(data.message);
      }
      setIsLoader(false);
    } catch (error) {
      setIsLoader(false);
    }
  };



  return (
    <div className="d-flex flex-column flex-column-fluid">
      {/* Toolbar */}
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              {id ? 'Edit' : 'Add New'} {cfg.singular}
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/dashboard" className="text-muted text-hover-primary">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">
                <Link to={cfg.basePath} className="text-muted text-hover-primary">{cfg.title}</Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Content */}
      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <nav>
              <div className="nav nav-tabs" id="nav-tab" role="tablist">
                <button
                  className={`nav-link fs-lg-5 ${tab === "info" ? "active" : ""}`}
                  onClick={() => setTab("info")}
                  id="nav-profile-tab"
                  type="button"
                >
                  <i className="bi bi-envelope-fill"></i> Company Info
                </button>
                
              </div>
            </nav>
            <div className="card-body pt-0">
              {/* Info Form */}
              {tab === "info" && (
                <form encType="multipart/form-data" method="POST" className="form fv-plugins-bootstrap5 fv-plugins-framework" onSubmit={handleSubmit}>
                  <div className="row g-9 my-3">
                    {/* Company Name */}
                    <div className="col-md-6 fv-row">
                      <label className="required fs-6 fw-semibold mb-2"> Name</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Enter  Name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>

               

                    {/* Email */}
                    <div className="col-md-6 fv-row">
                      <label className="required fs-6 fw-semibold mb-2">Email</label>
                      <input
                        type="email"
                        className="form-control form-control-solid"
                        placeholder="Enter Company Email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                    {/* Password */}
                    <div className="col-md-6 fv-row">
                      <label className="fs-6 fw-semibold mb-2">Password</label>
                      {id && <small className="text-info mx-3">(Leave empty if don't want to update password)</small>}
                      <input
                        type="password"
                        className="form-control form-control-solid"
                        placeholder="Company Password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">Service Type</label>
                      <select name="service_type" className="form-select" value={formData.service_type}
                        onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                        >
                        <option value="">Select Type</option>
                        <option value="Company">Company</option>
                        <option value="Individual">Individual</option>
                      </select>
                      {/* <Dropdown
                        value={formData.category_id}
                        options={categories}
                        optionLabel="title" 
                        optionValue="_id" 
                        onChange={(selectedOption) =>
                          setFormData({ ...formData, category_id: selectedOption.value })
                        }
                        placeholder="Select Category"
                        filter
                        checkmark={true}
                      /> */}
                      
                    </div>
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">Categories</label>
                      <MultiSelect
                        value={formData.categories}
                        options={categories}
                        optionLabel="title"
                        optionValue="_id"
                        onChange={(e) => setFormData({ ...formData, categories: e.value })}
                        placeholder="Select Categories"
                        filter
                        className="form-control"
                      />
                      
                    </div>
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className={`${isFacility ? '' : 'required '}fs-6 fw-semibold mb-2`}>Sub Category</label>
                      <MultiSelect
                        value={formData.sub_category_ids}
                        options={sub_categories}
                        optionLabel="title"
                        optionValue="_id"
                        onChange={(e) => setFormData({ ...formData, sub_category_ids: e.value })}
                        placeholder="Select Sub Categories"
                        filter
                        className="form-control"
                      />
                    </div>
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className={`${isFacility ? '' : 'required '}fs-6 fw-semibold mb-2`}>Emirates</label>
                      <MultiSelect
                        value={formData.emirates}
                        options={emirates}
                        optionLabel="name"
                        optionValue="_id"
                        onChange={(e) => setFormData({ ...formData, emirates: e.value })}
                        placeholder="Select Emirates"
                        filter
                        className="form-control"
                      />
                    </div>

                    {isFacility && (
                      <div className="col-md-12 fv-row">
                        <label className="required fs-6 fw-semibold mb-2">Service Areas / Communities</label>
                        <TagsInput
                          id="facility-areas"
                          value={formData.areas || []}
                          onChange={(areas) => setFormData({ ...formData, areas })}
                          suggestions={areaSuggestions}
                          placeholder="e.g. Dubai Marina — press Enter to add"
                        />
                        <div className="form-text">Residents with approved properties in these areas see this facility's services.</div>
                      </div>
                    )}

                    {/* Phone */}
                    <div className="col-md-6 fv-row">
                      <label className="required fs-6 fw-semibold mb-2">Phone</label>
                      <input
                        type="number"
                        className="form-control form-control-solid"
                        placeholder="971xxxxxxx"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6 fv-row">
                      <label className=" fs-6 fw-semibold mb-2">Secondary Phone</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Secondary Phone"
                        value={formData.secondary_phone}
                        onChange={(e) => setFormData({ ...formData, secondary_phone: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6 fv-row">
                      <label className="required fs-6 fw-semibold mb-2">License No</label>
                      <input
                        type="text" required
                        className="form-control form-control-solid"
                        placeholder="License No"
                        value={formData.license_no}
                        onChange={(e) => setFormData({ ...formData, license_no: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6 fv-row">
                      <label className="required fs-6 fw-semibold mb-2">Reg Year</label>
                      <input
                        type="number"
                        className="form-control form-control-solid"
                        placeholder="Reg Year"
                        value={formData.reg_date}
                        onChange={(e) => setFormData({ ...formData, reg_date: e.target.value })}
                      />
                    </div>
                    
                    
                    <div className="col-md-12 fv-row">
                      <label className="fs-6 fw-semibold mb-2">Location</label>
                      <LocationPicker
                        location={formData.location}
                        lat={formData.lat}
                        lng={formData.lng}
                        onChange={(loc) => setFormData((prev) => ({ ...prev, ...loc }))}
                      />
                    </div>

                    {/* Profile Picture */}
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">Profile Picture</label>
                      <ImageUpload 
                        name="picture" 
                        onImageChange={handleImageChange} 
                        defaultPreviewUrl={formData.picture} 
                      />
                    </div>
                  
                    <div className="col-md-12 fv-row">
                      <label className="required fs-6 fw-semibold mb-2">About</label>
                      <textarea name="about" className='form-control form-control-solid' rows={2}  placeholder='About Company...'
                        value={formData.about || ''}
                        onChange={(e) => setFormData({ ...formData, about: e.target.value })}>
                      </textarea>
                    </div>
                    
                    <div className="col-md-6 ">
                      <label className={`${isFacility ? '' : 'required '}fs-6 fw-semibold mb-2`}> License</label>

                      <FileDropzone
                        title="license"
                        oldFiles={formData.license!=null ?[formData.license]:[]}
                        updateOldImages={updateOldFiles}
                        setFiles={createSetFiles('license')}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className={`${isFacility ? '' : 'required '}fs-6 fw-semibold mb-2`}> Certificate</label>
                      <FileDropzone
                        title="certificate"
                        oldFiles={formData.certificate!=null ?[formData.certificate]:[]}
                        updateOldImages={updateOldFiles}
                        setFiles={createSetFiles('certificate')}
                      />
                    </div>
                  

                  </div>

                  {/* Actions */}
                  <div className="text-center mt-3">
                    <button type="button" className="btn btn-light me-3" onClick={() => navigate(cfg.basePath)}>
                      Back
                    </button>
                    <button type="submit" className="btn btn-base" disabled={isLoader}>
                      <span className="indicator-label" hidden={isLoader}>Submit</span>
                      {isLoader && <Loader size={20} text="Submitting" />}
                    </button>
                  </div>
                </form>
              )}


                {isModalOpen && <TimingModal branchId={id}  closeModal={handleCloseModal} />}

            </div>
          </div>
          
        </div>
      </div>
      
    </div>
  );
};

export default CompanyForm;
